<!-- src/pages/Taxi.svelte -->
<script>
  import { onMount, onDestroy } from 'svelte';
  import { currentLang } from '../lib/i18n';
  import { content } from '../lib/i18n/booking-content';
  import AutoServiceForm from '../components/AutoServiceForm.svelte';
  import TireServiceForm from '../components/TireServiceForm.svelte';
  import BookingConfirmation from '../components/BookingConfirmation.svelte';
  import LoadingSpinner from '../components/LoadingSpinner.svelte';
  import { gsap } from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';

  gsap.registerPlugin(ScrollTrigger);

  let lang = 'hu';
  currentLang.subscribe(value => {
    lang = value;
  });

  // Services pricing matching Services.svelte exactly
  const tireServicePrices = {
    fullServicePer4Tires: [
      { size: '16"', price: '18000 Ft' },
      { size: '17"', price: '19000 Ft' },
      { size: '18"', price: '21000 Ft' },
      { size: '19"', price: '23000 Ft' },
      { size: '20"', price: '24000 Ft' }
    ],
    patching: {
      name: { hu: 'Defektjavítás', en: 'Tire Patching' },
      pricePerTyre: '5000 Ft',
      note: { hu: '/ gumi + szerelés', en: '/ tire + mounting' }
    },
    wheelMountingOnly: {
      name: { hu: 'Kerék le-fel szerelés', en: 'Wheel Mounting Only' },
      pricePer4pcs: '5000 Ft',
      note: { hu: '/ 4 darab', en: '/ 4 pcs' }
    },
    alloyWheelMountingAlignment: {
      name: { hu: 'Kerék le-fel szerelés és centrírozás (könnyűfém felni)', en: 'Alloy Wheel Mounting & Alignment' },
      pricePer4pcs: '12000 Ft',
      note: { hu: '/ 4 darab', en: '/ 4 pcs' }
    }
  };

  const maintenanceServices = [
    { name: { hu: 'Autóvizsgálat', en: 'Car Inspection' } },
    { name: { hu: 'Fékjavítás', en: 'Brake Repair' } },
    { name: { hu: 'Futómű javítások', en: 'Chassis Repairs' } },
    { name: { hu: 'Kuplung javítás, kuplungcsere', en: 'Clutch Repair, Replacement' } },
    { name: { hu: 'Olajcsere, szűrők és váltó olajcsere', en: 'Oil Change, Filters, and Gearbox Oil Change' } },
    { name: { hu: 'Vezérműszíj csere', en: 'Timing Belt Replacement' } },
    { name: { hu: 'Váltó felújítás', en: 'Gearbox Overhaul' } },
    { name: { hu: 'Motorjavítás, felújítás', en: 'Engine Repair, Overhaul' } }
  ];

  // 5 Taxi Tiers
  const taxiTiers = [
    { id: 'Gyémánt', icon: '💎', label: { hu: 'Gyémánt', en: 'Diamond' }, desc: { hu: 'Kiemelt prioritás & legnagyobb kedvezmény', en: 'Highest priority & maximum discount' } },
    { id: 'Platina', icon: '⚪', label: { hu: 'Platina', en: 'Platinum' }, desc: { hu: 'Soron kívüli fogadás & kiemelt díjszabás', en: 'Fast-track intake & premium rates' } },
    { id: 'Arany', icon: '🥇', label: { hu: 'Arany', en: 'Gold' }, desc: { hu: 'Kiemelt taxi partner kedvezmény', en: 'Premium taxi partner discount' } },
    { id: 'Ezüst', icon: '🥈', label: { hu: 'Ezüst', en: 'Silver' }, desc: { hu: 'Flotta kedvezmény és garanciális javítások', en: 'Fleet discount & warranty repairs' } },
    { id: 'Bronz', icon: '🥉', label: { hu: 'Bronz', en: 'Bronze' }, desc: { hu: 'Alap taxi partner flotta szint', en: 'Base taxi partner fleet level' } }
  ];

  // Booking state
  let selectedTaxiService = 'autoService'; // 'autoService' | 'tireService'
  let selectedTaxiTier = 'Bronz';
  let isSubmitting = false;
  let submitError = null;
  let submitSuccess = false;
  let showConfirmation = false;
  let bookingDetails = {};

  const isDevelopment = typeof window !== 'undefined' && (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
  const BACKEND_API_URL = isDevelopment ? 'http://localhost:3001' : (import.meta.env.VITE_BACKEND_API_URL || 'https://atgroup-backend.fly.dev');

  function scrollToBooking(service = null) {
    if (service) selectedTaxiService = service;
    const el = document.getElementById('taxi-booking-section');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  async function handleBookingComplete(event) {
    if (isSubmitting) return;
    const formData = event.detail;

    if (!formData.contact?.email && !formData.email) {
      submitError = lang === 'hu' ? 'Email megadása kötelező' : 'Email is required';
      return;
    }

    isSubmitting = true;
    submitError = null;
    submitSuccess = false;
    showConfirmation = false;

    try {
      const activeTier = formData.taxiTier || selectedTaxiTier || 'Bronz';
      const emailData = {
        ...formData,
        service: formData.service,
        customerName: formData.name,
        email: formData.contact?.email || formData.email,
        customerEmail: formData.contact?.email || formData.email,
        customerPhone: formData.contact?.phone || formData.phone,
        phone: formData.contact?.phone || formData.phone,
        date: formData.date,
        time: formData.time || '',
        startDate: formData.startDate || formData.date,
        endDate: formData.endDate || formData.date,
        startTime: formData.startTime || formData.time || '12:00',
        endTime: formData.endTime || formData.time || '12:00',
        serviceType: formData.serviceType || '',
        carModel: formData.carModel || '',
        licensePlate: formData.licensePlate || '',
        chassisNumber: formData.chassisNumber || '',
        notes: formData.notes || '',
        tireCount: formData.tireCount || (formData.service === 'tireService' ? 4 : null),
        passengers: 1,
        isTaxi: true,
        needsTaxi: true,
        fromTaxiPage: true,
        taxiTier: activeTier,
        adminEmail: 'ahmed@atgroup.hu',
        createdAt: new Date().toISOString()
      };

      const apiUrl = BACKEND_API_URL.endsWith('/') ? BACKEND_API_URL.slice(0, -1) : BACKEND_API_URL;
      const response = await fetch(`${apiUrl}/api/send-booking-emails`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Server responded with status ${response.status}: ${errorText}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        submitSuccess = true;
        showConfirmation = true;
        bookingDetails = {
          ...emailData,
          id: result.data.id || 'N/A',
          referenceNumber: result.data.referenceNumber || result.data.id || 'N/A',
          bookingDate: new Date().toLocaleDateString(),
          bookingTime: new Date().toLocaleTimeString(),
          name: emailData.customerName,
          contact: {
            name: emailData.customerName,
            email: emailData.customerEmail,
            phone: emailData.customerPhone
          }
        };

        setTimeout(() => {
          const bookingEl = document.getElementById('taxi-booking-section');
          if (bookingEl) {
            bookingEl.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 80);
      } else {
        throw new Error(result.message || 'Server error');
      }
    } catch (err) {
      console.error('Error submitting taxi booking:', err);
      submitError = lang === 'hu'
        ? 'Hiba történt a szerverhez való kapcsolódás során.'
        : 'An error occurred while connecting to the server.';
      submitSuccess = false;
    } finally {
      isSubmitting = false;
    }
  }

  function resetBooking() {
    showConfirmation = false;
    submitError = null;
    submitSuccess = false;
    isSubmitting = false;
    bookingDetails = {};
  }

  onMount(() => {
    setTimeout(() => {
      ScrollTrigger.refresh();
      gsap.from('.service-section', {
        y: 40,
        opacity: 0,
        duration: 0.8,
        stagger: 0.25,
        scrollTrigger: {
          trigger: '.services-container',
          start: 'top 75%'
        }
      });
    }, 100);
  });

  onDestroy(() => {
    ScrollTrigger.getAll().forEach(t => t.kill());
  });
</script>

<!-- Hero Section with dark navy gradient so Header navigation is 100% visible -->
<section class="services-hero">
  <div class="hero-background"></div>
  <div class="container relative z-10">
    <h1>{$currentLang === 'hu' ? 'TAXISZERVIZ & GUMISZERVIZ' : 'TAXI & TIRE FLEET SERVICE'}</h1>
    <p>
      {$currentLang === 'hu'
        ? 'Gyors és megbízható gépjármű karbantartás, gumiszerviz és azonnali időpontfoglalás taxik és személyszállító flották számára'
        : 'Fast and dependable auto maintenance, tire services, and dedicated priority booking for taxi drivers and fleet operators'}
    </p>
    <div class="hero-actions">
      <button on:click={() => scrollToBooking('autoService')} class="btn btn-primary hero-btn">
        {$currentLang === 'hu' ? 'Autószerviz Foglalás' : 'Book Auto Service'}
      </button>
      <button on:click={() => scrollToBooking('tireService')} class="btn btn-secondary hero-btn">
        {$currentLang === 'hu' ? 'Gumiszerviz Foglalás' : 'Book Tire Service'}
      </button>
    </div>
  </div>
  <div class="hero-wave">
    <svg preserveAspectRatio="none" viewBox="0 0 1440 120" xmlns="http://www.w3.org/2000/svg">
      <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,42.7C1120,32,1280,32,1360,32L1440,32L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z"></path>
    </svg>
  </div>
</section>

<div class="services-container">

  <!-- Section 1: Autószerviz Árlista (Same as Services.svelte) -->
  <section class="service-section" id="maintenance">
    <div class="container">
      <div class="service-content centered-content">
        <span class="section-tag">{$currentLang === 'hu' ? 'AUTÓSZERVIZ ÁRLISTA' : 'AUTO SERVICE PRICING'}</span>
        <h2>{$currentLang === 'hu' ? 'MÁRKAFÜGGETLEN AUTÓSZERVIZ' : 'BRAND-AGNOSTIC CAR SERVICE'}</h2>
        <p class="service-description">
          {$currentLang === 'hu'
            ? 'Szakértő csapatunk széles körű szervizszolgáltatást nyújt minden autómárka számára, a rendszeres karbantartástól a komplex javításokig.'
            : 'Our expert team provides a wide range of maintenance services for all car brands, from routine maintenance to complex repairs.'}
        </p>

        <div class="labor-fee-card">
          <div class="labor-fee-label">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>
            </svg>
            <span>{$currentLang === 'hu' ? 'SZERVIZ MUNKADÍJ' : 'SERVICE LABOR FEE'}</span>
          </div>
          <div class="labor-fee-price">
            <span class="price-amount">20.000 Ft</span>
            <span class="price-suffix">+ ÁFA</span>
          </div>
        </div>
      </div>

      <div class="maintenance-bubbles-container">
        {#each maintenanceServices as service (service.name.hu)}
          <div class="maintenance-bubble">
            {service.name[$currentLang]}
          </div>
        {/each}
      </div>

      <div class="centered-button-container">
        <button on:click={() => scrollToBooking('autoService')} class="btn btn-primary">
          {$currentLang === 'hu' ? 'Autószerviz Időpontfoglalás' : 'Book Auto Service'}
        </button>
      </div>
    </div>
  </section>

  <!-- Section 2: Gumiszerviz Árlista (Same as Services.svelte) -->
  <section class="service-section" id="tire">
    <div class="container">
      <div class="service-grid">
        <div class="service-content">
          <span class="section-tag">{$currentLang === 'hu' ? 'GUMISZERVIZ ÁRLISTA' : 'TIRE SERVICE PRICING'}</span>
          <h2>{$currentLang === 'hu' ? 'GUMISZERVIZ' : 'TIRE SERVICE'}</h2>
          <p class="service-description">
            {$currentLang === 'hu'
              ? 'Szakértő technikusaink készen állnak az abroncsokkal kapcsolatos bármilyen probléma azonnali megoldására, hogy biztonságosan visszatérhessen az útra.'
              : 'Our expert technicians are ready to solve any tire-related problems immediately and get you safely back on the road.'}
          </p>
          <ul class="feature-list">
            {#each ($currentLang === 'hu'
              ? ['Gumiabroncs csere és javítás', 'Kerék kiegyensúlyozás', 'Szezonális tárolás']
              : ['Tire replacement and repair', 'Wheel balancing', 'Seasonal storage']
            ) as feature}
              <li>{feature}</li>
            {/each}
          </ul>
          <button on:click={() => scrollToBooking('tireService')} class="btn btn-primary">
            {$currentLang === 'hu' ? 'Gumiszerviz Foglalás' : 'Book Tire Service'}
          </button>
        </div>
        <div class="service-image">
          <img src="images/taxi.png" alt="Taxi Tire Service" />
        </div>
      </div>

      <h3 class="tire-main-service-title">
        {$currentLang === 'hu' ? 'Kerék le-fel szerelés, Gumiabroncs átszerelés és Centrírozás' : 'Wheel & Tire Fitting, Alignment'}
      </h3>
      <div class="pricing-table-container scrollable-table tire-table-container">
        <table class="pricing-table tire-service-main-table">
          <thead>
            <tr>
              <th>{$currentLang === 'hu' ? 'Méret' : 'Size'}</th>
              <th>{$currentLang === 'hu' ? 'Ár / 4 abroncs' : 'Price / 4 tires'}</th>
            </tr>
          </thead>
          <tbody>
            {#each tireServicePrices.fullServicePer4Tires as item (item.size)}
              <tr>
                <td>{item.size}</td>
                <td>{item.price}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>

      <div class="tire-services-grid">
        {#if tireServicePrices.patching}
          <div class="tire-service-block">
            <h3>{tireServicePrices.patching.name[$currentLang]}</h3>
            <div class="service-price">
              {tireServicePrices.patching.pricePerTyre}
              <span class="per-set">{tireServicePrices.patching.note[$currentLang]}</span>
            </div>
          </div>
        {/if}

        {#if tireServicePrices.wheelMountingOnly}
          <div class="tire-service-block">
            <h3>{tireServicePrices.wheelMountingOnly.name[$currentLang]}</h3>
            <div class="service-price">
              {tireServicePrices.wheelMountingOnly.pricePer4pcs}
              <span class="per-set">{tireServicePrices.wheelMountingOnly.note[$currentLang]}</span>
            </div>
          </div>
        {/if}

        {#if tireServicePrices.alloyWheelMountingAlignment}
          <div class="tire-service-block">
            <h3>{tireServicePrices.alloyWheelMountingAlignment.name[$currentLang]}</h3>
            <div class="service-price">
              {tireServicePrices.alloyWheelMountingAlignment.pricePer4pcs}
              <span class="per-set">{tireServicePrices.alloyWheelMountingAlignment.note[$currentLang]}</span>
            </div>
          </div>
        {/if}
      </div>
    </div>
  </section>

  <!-- Section 3: Dedicated Taxi Booking Form -->
  <section class="service-section taxi-booking-wrapper" id="taxi-booking-section">
    <div class="container">
      <div class="service-content centered-content">
        <h2>
          {$currentLang === 'hu'
            ? 'ONLINE IDŐPONTFOGLALÁS TAXISOKNAK'
            : 'ONLINE APPOINTMENT BOOKING FOR TAXIS'}
        </h2>
        <p class="service-description">
          {$currentLang === 'hu'
            ? 'Válasszon szolgáltatást és partneri fokozati szintet az azonnali naptári rögzítéshez és a flotta kedvezmények érvényesítéséhez.'
            : 'Select your service and partner tier for instant calendar booking and applied fleet perks.'}
        </p>
      </div>

      <!-- Service Type Tabs (Autószerviz / Gumiszerviz) WITHOUT emojis -->
      <div class="taxi-service-tabs">
        <button
          type="button"
          class="taxi-tab-btn"
          class:active={selectedTaxiService === 'autoService'}
          on:click={() => selectedTaxiService = 'autoService'}
        >
          <span class="tab-title">{$currentLang === 'hu' ? 'Autószerviz' : 'Auto Service'}</span>
        </button>
        <button
          type="button"
          class="taxi-tab-btn"
          class:active={selectedTaxiService === 'tireService'}
          on:click={() => selectedTaxiService = 'tireService'}
        >
          <span class="tab-title">{$currentLang === 'hu' ? 'Gumiszerviz' : 'Tire Service'}</span>
        </button>
      </div>

      <!-- Direct Form or Confirmation -->
      <div class="taxi-booking-card">
        {#if !showConfirmation}
          {#if submitError}
            <div class="error-banner">
              <p>{submitError}</p>
            </div>
          {/if}

          {#if isSubmitting}
            <div class="submitting-overlay">
              <LoadingSpinner size="2.5rem" color="rgb(27, 42, 75)" />
              <p class="submitting-text">
                {$currentLang === 'hu' ? 'Taxi foglalás rögzítése és naptári bejegyzése...' : 'Recording taxi appointment in calendar...'}
              </p>
            </div>
          {/if}

          {#if selectedTaxiService === 'autoService'}
            <AutoServiceForm
              {content}
              currentLang={lang}
              forceTaxi={true}
              initialTaxiTier={selectedTaxiTier}
              on:bookingComplete={handleBookingComplete}
            />
          {:else if selectedTaxiService === 'tireService'}
            <TireServiceForm
              {content}
              currentLang={lang}
              forceTaxi={true}
              initialTaxiTier={selectedTaxiTier}
              on:bookingComplete={handleBookingComplete}
            />
          {/if}
        {:else}
          <BookingConfirmation
            bookingDetails={bookingDetails}
            {content}
            currentLang={lang}
            resetBooking={resetBooking}
          />
        {/if}
      </div>
    </div>
  </section>

</div>

<style>
  /* ── Hero matching Services.svelte with dark navy gradient ── */
  .services-hero {
    position: relative;
    background: linear-gradient(135deg, rgb(15, 23, 42) 0%, rgb(27, 42, 75) 100%);
    color: #ffffff;
    padding: 10rem 2rem 7rem;
    text-align: center;
    overflow: hidden;
  }

  .hero-background {
    position: absolute;
    inset: 0;
    background-image: radial-gradient(circle at top right, rgba(255, 255, 255, 0.08) 0%, transparent 40%),
                      radial-gradient(circle at bottom left, rgba(255, 255, 255, 0.04) 0%, transparent 40%);
    pointer-events: none;
  }

  .relative {
    position: relative;
  }

  .z-10 {
    z-index: 10;
  }

  .services-hero h1 {
    font-size: 3.25rem;
    font-weight: 800;
    margin-bottom: 1.25rem;
    letter-spacing: -0.02em;
    line-height: 1.15;
    color: #ffffff;
  }

  .services-hero p {
    font-size: 1.2rem;
    opacity: 0.9;
    max-width: 740px;
    margin: 0 auto 2rem;
    line-height: 1.6;
    color: #f1f5f9;
  }

  .hero-actions {
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
  }

  .hero-btn {
    padding: 0.85rem 1.75rem;
    font-weight: 600;
    font-size: 1rem;
    border-radius: 50px;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .btn-secondary {
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    border: 1px solid rgba(255, 255, 255, 0.3);
  }

  .btn-secondary:hover {
    background: rgba(255, 255, 255, 0.22);
    transform: translateY(-2px);
  }

  .hero-wave {
    position: absolute;
    bottom: -1px;
    left: 0;
    width: 100%;
    overflow: hidden;
    line-height: 0;
  }

  .hero-wave svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
    height: 70px;
    fill: #ffffff;
  }

  /* ── Container & Sections ── */
  .services-container {
    background-color: #ffffff;
    position: relative;
    z-index: 20;
    padding-bottom: 5rem;
  }

  .service-section {
    padding: 4.5rem 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  }

  .container {
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 1.5rem;
  }

  .centered-content {
    text-align: center;
    max-width: 820px;
    margin: 0 auto 3rem;
  }

  .section-tag {
    display: inline-block;
    font-size: 0.8rem;
    font-weight: 700;
    color: #ca8a04;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 0.5rem;
  }

  h2 {
    font-size: 2.25rem;
    font-weight: 800;
    color: rgb(27, 42, 75);
    margin-bottom: 1rem;
    line-height: 1.25;
  }

  .service-description {
    font-size: 1.1rem;
    color: #4b5563;
    line-height: 1.7;
  }

  /* ── Autószerviz Labor Fee Card & Bubbles ── */
  .labor-fee-card {
    margin: 2rem auto;
    padding: 1.25rem 2.5rem;
    background: #ffffff;
    border-radius: 14px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.05);
    border: 1px solid rgba(0, 0, 0, 0.08);
    display: inline-flex;
    align-items: center;
    gap: 2rem;
  }

  .labor-fee-label {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    color: rgb(27, 42, 75);
    font-weight: 700;
    font-size: 0.95rem;
  }

  .labor-fee-price {
    display: flex;
    align-items: baseline;
    gap: 0.4rem;
  }

  .price-amount {
    font-size: 1.75rem;
    font-weight: 800;
    color: #ca8a04;
  }

  .price-suffix {
    font-size: 0.9rem;
    font-weight: 600;
    color: #64748b;
  }

  .maintenance-bubbles-container {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 0.75rem;
    max-width: 900px;
    margin: 0 auto 2.5rem;
  }

  .maintenance-bubble {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    color: rgb(27, 42, 75);
    padding: 0.65rem 1.25rem;
    border-radius: 50px;
    font-size: 0.95rem;
    font-weight: 600;
    transition: all 0.2s ease;
  }

  .maintenance-bubble:hover {
    background: #ffffff;
    border-color: #ca8a04;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.06);
  }

  .centered-button-container {
    text-align: center;
    margin-top: 1.5rem;
  }

  /* ── Gumiszerviz Grid & Table ── */
  .service-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 3.5rem;
    align-items: center;
    margin-bottom: 3.5rem;
  }

  .service-image {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .service-image img {
    width: 100%;
    max-width: 380px;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    border-radius: 16px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 1.5rem 0 2rem;
  }

  .feature-list li {
    padding: 0.5rem 0 0.5rem 1.75rem;
    position: relative;
    color: #374151;
    font-size: 1rem;
    line-height: 1.5;
  }

  .feature-list li::before {
    content: "✓";
    position: absolute;
    left: 0;
    color: #ca8a04;
    font-weight: 700;
  }

  .tire-main-service-title {
    text-align: center;
    font-size: 1.35rem;
    font-weight: 700;
    color: rgb(27, 42, 75);
    margin: 3rem 0 1.5rem;
  }

  .pricing-table-container {
    background: #ffffff;
    border-radius: 14px;
    border: 1px solid #e2e8f0;
    overflow-x: auto;
    margin-bottom: 2.5rem;
    box-shadow: 0 4px 16px rgba(0,0,0,0.03);
  }

  .pricing-table {
    width: 100%;
    border-collapse: collapse;
    text-align: left;
  }

  .pricing-table th, .pricing-table td {
    padding: 1rem 1.5rem;
    border-bottom: 1px solid #f1f5f9;
  }

  .pricing-table th {
    background: #f8fafc;
    font-size: 0.85rem;
    font-weight: 700;
    color: #475569;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  .pricing-table td {
    font-size: 0.95rem;
    color: #1e293b;
    font-weight: 500;
  }

  .tire-services-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
    gap: 1.5rem;
    margin-top: 1.5rem;
  }

  .tire-service-block {
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    text-align: center;
  }

  .tire-service-block h3 {
    font-size: 1.05rem;
    font-weight: 700;
    color: rgb(27, 42, 75);
    margin-bottom: 0.75rem;
  }

  .service-price {
    font-size: 1.5rem;
    font-weight: 800;
    color: #ca8a04;
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .per-set {
    font-size: 0.85rem;
    font-weight: 500;
    color: #64748b;
    margin-top: 0.25rem;
  }

  /* ── Dedicated Taxi Booking Section ── */
  .taxi-booking-wrapper {
    background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
    border-bottom: none;
    padding: 5rem 0;
    scroll-margin-top: 90px;
  }

  .taxi-service-tabs {
    display: flex;
    justify-content: center;
    gap: 1rem;
    margin-bottom: 2.5rem;
  }

  .taxi-tab-btn {
    display: flex;
    align-items: center;
    gap: 0.6rem;
    padding: 0.9rem 2rem;
    border-radius: 50px;
    border: 2px solid #e2e8f0;
    background: #ffffff;
    color: #475569;
    font-size: 1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .taxi-tab-btn:hover {
    border-color: #ca8a04;
    color: #854d0e;
    transform: translateY(-2px);
  }

  .taxi-tab-btn.active {
    background: rgb(27, 42, 75);
    border-color: rgb(27, 42, 75);
    color: #ffffff;
    box-shadow: 0 6px 20px rgba(27, 42, 75, 0.2);
  }

  .taxi-booking-card {
    background: #ffffff;
    border-radius: 20px;
    border: 1px solid #e2e8f0;
    box-shadow: 0 10px 40px rgba(0, 0, 0, 0.05);
    padding: 2.5rem;
    max-width: 900px;
    margin: 0 auto;
    position: relative;
  }

  .submitting-overlay {
    position: absolute;
    inset: 0;
    background: rgba(255, 255, 255, 0.92);
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    z-index: 50;
    gap: 1rem;
  }

  .submitting-text {
    font-size: 1.1rem;
    font-weight: 600;
    color: rgb(27, 42, 75);
  }

  .error-banner {
    background: #fef2f2;
    border: 1px solid #f87171;
    color: #b91c1c;
    padding: 1rem 1.25rem;
    border-radius: 10px;
    margin-bottom: 1.5rem;
    text-align: center;
    font-weight: 600;
  }

  .btn-primary {
    background: rgb(27, 42, 75);
    color: #ffffff;
    border: none;
    padding: 0.85rem 2rem;
    border-radius: 50px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.25s ease;
  }

  .btn-primary:hover {
    background: rgb(45, 68, 115);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(27, 42, 75, 0.2);
  }

  @media (max-width: 768px) {
    .services-hero h1 {
      font-size: 2.25rem;
    }
    .service-grid {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
    .labor-fee-card {
      flex-direction: column;
      gap: 0.75rem;
      padding: 1.25rem;
    }
    .taxi-booking-card {
      padding: 1.25rem;
    }
  }
</style>
